/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import en from "./assets/json/en.json";
import zh from "./assets/json/zh.json";
import cn from "./assets/json/cn.json";

// Detects when the browser is running a stale cached build. version.json is
// written into every build by checkplus_bash/write_version.js; if the bundle
// it names differs from the one currently loaded, reload once to pick up the
// new deploy. Two independent guards prevent a reload loop when the reload
// doesn't resolve the mismatch (stale CDN edge, sessionStorage unavailable):
// the sessionStorage marker, and never auto-reloading a page that was itself
// loaded by a reload. When a guard blocks the auto-reload, a persistent
// "Update" banner is shown instead so the user can retry manually.

const RELOADED_FOR_KEY = "versionCheck.reloadedFor";
const BANNER_ID = "versionCheck-updateBanner";

function getTextObj() {
  let lang = "en";
  try {
    lang = localStorage.getItem("i18nextLng") || "en";
  } catch (e) {
    // localStorage unavailable — fall back to English
  }
  const locale = lang === "zh" ? zh : lang === "cn" ? cn : en;
  // i18n.js uppercases the top-level keys of these shared JSON objects at
  // init, so the section may live under either casing depending on timing.
  return (
    (locale && (locale.VersionCheck || locale.VERSIONCHECK)) ||
    en.VersionCheck || en.VERSIONCHECK || {}
  );
}

function showUpdateBanner() {
  if (document.getElementById(BANNER_ID)) return;
  const textObj = getTextObj();

  const banner = document.createElement("div");
  banner.id = BANNER_ID;
  banner.style.cssText =
    "position:fixed;bottom:16px;left:50%;transform:translateX(-50%);" +
    "z-index:2147483647;background:#1a1a2e;color:#ffffff;padding:10px 16px;" +
    "border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.3);display:flex;" +
    "align-items:center;gap:12px;font-size:14px;max-width:90vw;";

  const message = document.createElement("span");
  message.textContent = textObj.updateAvailable || "A new version of the app is available.";

  const button = document.createElement("button");
  button.textContent = textObj.updateNow || "Update";
  button.style.cssText =
    "background:#ffffff;color:#1a1a2e;border:none;border-radius:6px;" +
    "padding:6px 14px;font-size:14px;font-weight:600;cursor:pointer;";
  button.onclick = () => {
    try {
      sessionStorage.removeItem(RELOADED_FOR_KEY);
    } catch (e) {
      // ignore — reload works regardless
    }
    window.location.reload();
  };

  banner.appendChild(message);
  banner.appendChild(button);
  document.body.appendChild(banner);
}

export function checkAppVersion() {
  const script = document.querySelector('script[src*="/static/js/main."]');
  if (!script) return; // dev server — bundle is not hash-named

  const runningBundle = script.getAttribute("src").split("/").pop();

  fetch("/version.json?ts=" + Date.now(), { cache: "no-store" })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      if (!data || !data.bundle || data.bundle === runningBundle) return;

      const nav = performance.getEntriesByType("navigation")[0];
      const loadWasReload = nav && nav.type === "reload";
      let alreadyReloadedFor = null;
      try {
        alreadyReloadedFor = sessionStorage.getItem(RELOADED_FOR_KEY);
      } catch (e) {
        // sessionStorage unavailable — rely on the reload-type guard
      }

      if (loadWasReload || alreadyReloadedFor === data.bundle) {
        // Auto-reload already tried (or the user just reloaded manually) and
        // we are still stale — hand control to the user instead of looping.
        showUpdateBanner();
        return;
      }

      try {
        sessionStorage.setItem(RELOADED_FOR_KEY, data.bundle);
      } catch (e) {
        // sessionStorage unavailable — the reload-type guard still stops loops
      }
      window.location.reload();
    })
    .catch(() => {});
}
